import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { SystemActivity } from "../../../models/home";
import { LanguageService } from "../../../services/language/language.service";
import { HomeInteractionStateService } from "../../../services/home-ia/home-interaction-state.service";
import {
  HomeActivityDisplay,
  HomeActivityDisplayUxService
} from "../../../services/home-ia/home-activity-display-ux.service";
import { UxError } from "../../../models/ux-error.model";

@Component({
  selector: "app-home-last-activity",
  templateUrl: "./home-last-activity.component.html",
  styleUrls: [
    "./home-last-activity.component.shell-and-items.scss",
    "./home-last-activity.component.panel-and-responsive.scss"
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomeLastActivityComponent {
  @Input() activity: SystemActivity | null = null;
  @Input() history: readonly SystemActivity[] | null = null;
  @Input() loading = false;
  @Input() error: UxError | null = null;
  @Input() empty = false;
  @Input() compactLauncher = false;
  @Input() historyLoading = false;
  @Input() historyPage = 1;
  @Input() historyTotalPages = 1;
  @Output() retryRequest = new EventEmitter<void>();
  @Output() historyPageChange = new EventEmitter<number>();

  readonly activityExpanded$ = this.interactionState.activityExpanded$;
  readonly activityDetail$ = this.interactionState.activityDetail$;
  readonly historyPanelOpen$ = this.interactionState.historyPanelOpen$;
  private readonly displayCache = new Map<string, HomeActivityDisplay>();

  constructor(
    public lang: LanguageService,
    private readonly interactionState: HomeInteractionStateService,
    private readonly activityDisplayUx: HomeActivityDisplayUxService
  ) {}

  get typeLabel(): string | null {
    return this.activity ? this.lang.translate(`home.lastActivity.type.${this.activity.type}`) : null;
  }

  get previewHistory(): readonly SystemActivity[] {
    if (this.history?.length) {
      return this.history.slice(0, 3);
    }
    return this.activity ? [this.activity] : [];
  }

  getPreviewActionLabel(item: SystemActivity): string {
    return this.isSelectedActivity(item)
      ? this.lang.translate("common.close")
      : this.lang.translate("home.lastActivity.previewAction");
  }

  getActivityDisplay(item: SystemActivity): HomeActivityDisplay {
    const key = `${item.timestamp}|${item.description}|${item.impact.summary}`;
    const cached = this.displayCache.get(key);

    if (cached) {
      return cached;
    }

    const display = this.activityDisplayUx.getDisplay(item);
    this.displayCache.set(key, display);
    return display;
  }

  onActivityCardClick(item: SystemActivity): void {
    const current = this.interactionState.activityDetailSnapshot;
    if (current && current.timestamp === item.timestamp) {
      this.interactionState.setActivityDetail(null);
      if (this.compactLauncher) {
        this.interactionState.setActivityExpanded(true);
      }
      return;
    }
    this.interactionState.setActivityDetail(item);
    if (this.compactLauncher) {
      this.interactionState.setActivityExpanded(true);
    }
  }

  onOpenActivity(): void {
    this.interactionState.setActivityExpanded(true);
  }

  onViewHistory(): void {
    this.interactionState.toggleHistoryPanel(true);
  }

  onCloseDetail(): void {
    this.interactionState.setActivityDetail(null);
    if (this.compactLauncher) {
      this.interactionState.setActivityExpanded(true);
    }
  }

  onRetry(): void {
    this.retryRequest.emit();
  }

  onCloseHistory(): void {
    this.interactionState.toggleHistoryPanel(false);
  }

  onCloseActivity(): void {
    this.interactionState.toggleHistoryPanel(false);
    this.interactionState.setActivityDetail(null);
    this.interactionState.setActivityExpanded(false);
  }

  onActivityPanelInteraction(event: MouseEvent): void {
    event.stopPropagation();
  }

  onHistoryPanelInteraction(event: MouseEvent): void {
    event.stopPropagation();
  }

  onSelectHistory(item: SystemActivity): void {
    this.interactionState.setActivityDetail(item);
    this.interactionState.toggleHistoryPanel(false);
  }

  onHistoryPrev(): void {
    if (this.historyPage <= 1) {
      return;
    }
    this.historyPageChange.emit(this.historyPage - 1);
  }

  onHistoryNext(): void {
    if (this.historyPage >= this.historyTotalPages) {
      return;
    }
    this.historyPageChange.emit(this.historyPage + 1);
  }

  private isSelectedActivity(item: SystemActivity): boolean {
    const current = this.interactionState.activityDetailSnapshot;
    return !!current && current.timestamp === item.timestamp && current.description === item.description;
  }
}
